import React, { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Music, Plus, Trash2, ExternalLink, X, RefreshCw, Save, ListMusic, ShieldCheck, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../lib/logger';
import { parseSpotifyUrl, PLAYLIST_CATEGORIES, categoryMeta } from '../lib/spotify';

const EMPTY = {
  id: null,
  title: '',
  description: '',
  spotify_url: '',
  category: 'pregame',
  order: 100,
  active: false,           // Phase 23 — new playlists default to hidden
  clean_verified: false,   //          + unverified until admin affirms.
};

export default function AdminPlaylists() {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(null);

  const loadPlaylists = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/admin/playlists');
      setPlaylists(data?.playlists || []);
    } catch (err) {
      logger.error('AdminPlaylists load failed', err);
      toast.error('Could not load playlists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPlaylists(); }, []);

  const openNew = () => setEditor({ ...EMPTY });
  const openEdit = (p) => setEditor({
    id: p.id,
    title: p.title || '',
    description: p.description || '',
    spotify_url: p.spotify_url || '',
    category: p.category || 'pregame',
    order: p.order ?? 100,
    active: p.active !== false,
    clean_verified: !!p.clean_verified,
  });
  const closeEditor = () => setEditor(null);

  const updateField = (field, value) => setEditor((prev) => prev && { ...prev, [field]: value });

  const urlValidity = useMemo(() => {
    if (!editor) return 'idle';
    if (!editor.spotify_url) return 'idle';
    return parseSpotifyUrl(editor.spotify_url) ? 'valid' : 'invalid';
  }, [editor]);

  const handleSave = async () => {
    if (!editor) return;
    if (!editor.title.trim()) { toast.error('Title is required'); return; }
    if (!parseSpotifyUrl(editor.spotify_url)) {
      toast.error('Spotify URL must look like https://open.spotify.com/playlist/<id>');
      return;
    }
    // Phase 23 — clean-lyrics gate. Mirror the backend rule so the user gets
    // an immediate, in-form error instead of a generic 400 from the API.
    if (editor.active && !editor.clean_verified) {
      toast.error('Confirm "Clean lyrics verified" before making this playlist public.');
      return;
    }
    setSaving(true);
    const payload = {
      title: editor.title.trim(),
      description: editor.description.trim() || null,
      spotify_url: editor.spotify_url.trim(),
      category: editor.category || 'pregame',
      order: Number(editor.order) || 100,
      active: !!editor.active,
      clean_verified: !!editor.clean_verified,
    };
    try {
      let result;
      if (editor.id) {
        result = await api.patch(`/api/admin/playlists/${editor.id}`, payload);
      } else {
        result = await api.post('/api/admin/playlists', payload);
      }
      toast.success(editor.id ? 'Playlist updated' : 'Playlist added');
      closeEditor();
      if (result?.playlist) {
        setPlaylists((prev) => {
          const without = prev.filter((p) => p.id !== result.playlist.id);
          return [...without, result.playlist].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        });
      } else {
        loadPlaylists();
      }
    } catch (err) {
      toast.error(err.message || 'Could not save playlist');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    // Phase 23 — turning a playlist ON requires it to be verified clean first.
    // Catch this in the UI so the admin sees a clear coaching message rather
    // than a generic 400 from the backend.
    if (!p.active && !p.clean_verified) {
      toast.error('This playlist is not yet "Clean lyrics verified." Open it, confirm every track is clean, then save.', { duration: 5000 });
      return;
    }
    try {
      const result = await api.patch(`/api/admin/playlists/${p.id}`, { active: !p.active });
      if (result?.playlist) {
        setPlaylists((prev) => prev.map((x) => (x.id === result.playlist.id ? result.playlist : x)));
      }
      toast.success(p.active ? 'Playlist hidden' : 'Playlist now active');
    } catch (err) {
      toast.error(err.message || 'Could not update');
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.del(`/api/admin/playlists/${p.id}`);
      toast.success('Playlist deleted');
      setPlaylists((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      toast.error(err.message || 'Could not delete');
    }
  };

  const handleSeedDefaults = async () => {
    setSaving(true);
    try {
      const result = await api.post('/api/admin/playlists/seed-defaults', {});
      const inserted = result?.inserted ?? 0;
      const updated = result?.updated ?? 0;
      if (inserted > 0) {
        toast.success(`Added ${inserted} default playlist${inserted === 1 ? '' : 's'}.`);
      } else if (updated > 0) {
        toast.success(`Refreshed ${updated} existing playlist${updated === 1 ? '' : 's'}.`);
      } else {
        toast.success('Defaults are already in place.');
      }
      loadPlaylists();
    } catch (err) {
      toast.error(err.message || 'Could not restore defaults');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-playlists">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-foreground/60 font-semibold mb-1">
            The Locker Room
          </div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">Music playlists</h2>
          <p className="text-[13.5px] text-foreground/60 mt-1">
            Curated Spotify playlists shown in the floating mini-player and on <a href="/playlists" className="underline text-[#004C54]">/playlists</a>. Uses public embeds — no Spotify credentials required.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadPlaylists} disabled={loading} data-testid="admin-playlists-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={openNew} className="bg-[#004C54] hover:bg-[#003a40] text-white" data-testid="admin-playlists-new">
            <Plus className="h-4 w-4 mr-1.5" />
            Add playlist
          </Button>
        </div>
      </div>

      {/* Phase 23 — clean-lyrics curation banner. Permanent, not dismissable.
          Apex Athletic Mind serves middle-school and high-school athletes, so
          every published playlist needs verification. */}
      <div
        className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 sm:p-5"
        data-testid="admin-playlists-clean-banner"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
            <ShieldAlert className="h-5 w-5 text-amber-700" />
          </div>
          <div className="flex-1">
            <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-amber-800 font-bold mb-1">
              Clean Lyrics Policy
            </div>
            <p className="text-[13.5px] text-amber-900 leading-[1.55]">
              Apex Athletic Mind serves middle-school and high-school athletes. <strong>Every playlist
              must be personally verified to contain clean/radio-edit tracks only</strong> before it can
              be shown publicly. Recommended workflow:
            </p>
            <ol className="list-decimal list-inside mt-2 space-y-0.5 text-[13px] text-amber-900">
              <li>Build the playlist in your own Spotify account using clean versions only.</li>
              <li>Play every track end-to-end at least once.</li>
              <li>Paste the link here and tick <em>&ldquo;I&rsquo;ve verified every track is clean/radio-edit.&rdquo;</em></li>
              <li>If you ever change the underlying Spotify playlist, verification auto-revokes &mdash; re-verify before re-publishing.</li>
            </ol>
          </div>
        </div>
      </div>


      <Card>
        <CardContent className="p-0">
          {loading && playlists.length === 0 ? (
            <div className="p-8 text-center text-sm text-foreground/60" data-testid="admin-playlists-loading">Loading…</div>
          ) : playlists.length === 0 ? (
            <div className="p-10 text-center" data-testid="admin-playlists-empty">
              <ListMusic className="h-10 w-10 mx-auto text-foreground/30 mb-4" />
              <h3 className="font-heading text-[1.1rem] font-semibold mb-1">No playlists yet</h3>
              <p className="text-sm text-foreground/60 max-w-[46ch] mx-auto mb-5">
                Drop in Coach Lewis's 4 curated Spotify playlists with one click, or add them yourself.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button
                  onClick={handleSeedDefaults}
                  disabled={saving}
                  className="bg-[#004C54] hover:bg-[#003a40] text-white"
                  data-testid="admin-playlists-seed-defaults"
                >
                  <Music className="h-4 w-4 mr-1.5" />
                  {saving ? 'Restoring…' : "Restore Coach Lewis's defaults"}
                </Button>
                <Button variant="outline" onClick={openNew} data-testid="admin-playlists-empty-new">
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add manually
                </Button>
              </div>
              <p className="text-[11px] text-foreground/45 mt-4">
                The defaults are: Pre-Game Lock-In · In The Zone · Recovery &amp; Reset · Slow Down
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border/60">
              {playlists.map((p) => (
                <li key={p.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`admin-playlist-row-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2 mb-1">
                      {p.active ? (
                        <Badge className="bg-[#004C54]/10 text-[#004C54] hover:bg-[#004C54]/10 border-0">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="text-foreground/55">Hidden</Badge>
                      )}
                      {p.clean_verified ? (
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-0 inline-flex items-center gap-1" data-testid={`admin-playlist-verified-${p.id}`}>
                          <ShieldCheck className="h-3 w-3" />
                          Clean Verified
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 border-0 inline-flex items-center gap-1" data-testid={`admin-playlist-unverified-${p.id}`}>
                          <ShieldAlert className="h-3 w-3" />
                          Not Verified
                        </Badge>
                      )}
                      <span className="font-mono-accent uppercase tracking-[0.18em] text-[9px] text-foreground/55 font-semibold">
                        {categoryMeta(p.category).label}
                      </span>
                      <span className="text-[10.5px] text-foreground/40">order {p.order ?? 0}</span>
                    </div>
                    <div className="font-heading text-[15px] font-semibold truncate">{p.title}</div>
                    <div className="text-[12px] text-foreground/55 truncate mt-0.5">
                      {p.spotify_kind || 'playlist'} · {p.spotify_id}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button asChild variant="outline" size="sm">
                      <a href={p.spotify_url} target="_blank" rel="noopener noreferrer" data-testid={`admin-playlist-open-${p.id}`}>
                        Open <ExternalLink className="h-3 w-3 ml-1 opacity-60" />
                      </a>
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => toggleActive(p)} data-testid={`admin-playlist-toggle-${p.id}`}>
                      {p.active ? 'Hide' : 'Activate'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`admin-playlist-edit-${p.id}`}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(p)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`admin-playlist-delete-${p.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {editor && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto p-4 sm:p-6"
          onClick={(e) => { if (e.target === e.currentTarget) closeEditor(); }}
          data-testid="admin-playlists-editor"
        >
          <div className="w-full max-w-lg bg-background rounded-2xl shadow-2xl my-4">
            <div className="sticky top-0 bg-background rounded-t-2xl border-b border-border/60 px-5 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="font-heading text-lg font-semibold inline-flex items-center gap-2">
                  <Music className="h-4 w-4 text-[#004C54]" />
                  {editor.id ? 'Edit playlist' : 'Add playlist'}
                </h3>
              </div>
              <button onClick={closeEditor} className="p-2 rounded-lg hover:bg-secondary/60" data-testid="admin-playlists-editor-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 py-5 space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="pl-title">Title *</Label>
                <Input
                  id="pl-title"
                  value={editor.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder="Pre-Game Lock-In"
                  data-testid="admin-playlist-field-title"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="pl-url">Spotify URL *</Label>
                <Input
                  id="pl-url"
                  value={editor.spotify_url}
                  onChange={(e) => updateField('spotify_url', e.target.value)}
                  placeholder="https://open.spotify.com/playlist/..."
                  data-testid="admin-playlist-field-url"
                />
                <p className={`text-[11.5px] ${
                  urlValidity === 'invalid'
                    ? 'text-red-600'
                    : urlValidity === 'valid'
                      ? 'text-[#004C54]'
                      : 'text-foreground/55'
                }`}>
                  {urlValidity === 'valid'
                    ? 'Valid Spotify link — will use the public embed.'
                    : urlValidity === 'invalid'
                      ? 'Doesn’t look like a Spotify URL.'
                      : 'Right-click any Spotify playlist/track and choose Share → Copy link.'}
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="pl-category">Category</Label>
                  <select
                    id="pl-category"
                    value={editor.category}
                    onChange={(e) => updateField('category', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="admin-playlist-field-category"
                  >
                    {PLAYLIST_CATEGORIES.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pl-order">Display order</Label>
                  <Input
                    id="pl-order"
                    type="number"
                    value={editor.order}
                    onChange={(e) => updateField('order', e.target.value)}
                    placeholder="100"
                    data-testid="admin-playlist-field-order"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="pl-desc">Short description</Label>
                <Textarea
                  id="pl-desc"
                  value={editor.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={2}
                  placeholder="Walk-out energy. Compete-mode music."
                  data-testid="admin-playlist-field-desc"
                />
              </div>

              <Separator />

              {/* Phase 23 — Clean lyrics verification gate.
                  Admin must affirm this before the playlist can be published.
                  Mirrors the backend rule: active=true requires clean_verified=true. */}
              <div
                className={`rounded-xl border p-4 transition-colors ${
                  editor.clean_verified
                    ? 'border-emerald-200 bg-emerald-50/50'
                    : 'border-amber-200 bg-amber-50/60'
                }`}
                data-testid="admin-playlist-clean-block"
              >
                <div className="flex items-start gap-3">
                  {editor.clean_verified ? (
                    <ShieldCheck className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
                  ) : (
                    <ShieldAlert className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <Label
                        htmlFor="pl-clean"
                        className={`font-heading text-[14px] font-bold cursor-pointer ${
                          editor.clean_verified ? 'text-emerald-900' : 'text-amber-900'
                        }`}
                      >
                        Clean lyrics verified
                      </Label>
                      <Switch
                        id="pl-clean"
                        checked={!!editor.clean_verified}
                        onCheckedChange={(checked) => {
                          updateField('clean_verified', checked);
                          // If admin un-verifies, auto-hide. Active+unverified is illegal.
                          if (!checked && editor.active) updateField('active', false);
                        }}
                        data-testid="admin-playlist-field-clean-verified"
                      />
                    </div>
                    <p className={`text-[12.5px] leading-[1.55] ${
                      editor.clean_verified ? 'text-emerald-900/85' : 'text-amber-900/90'
                    }`}>
                      {editor.clean_verified
                        ? <>You&rsquo;ve confirmed every track in this playlist is clean / radio-edit. The playlist is eligible to be published to the public Locker Room.</>
                        : <>By turning this on you affirm: <em>&ldquo;I have personally listened to or otherwise verified every track in this playlist, and all tracks are clean / radio-edit versions appropriate for middle-school and high-school athletes.&rdquo;</em></>}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    id="pl-active"
                    checked={!!editor.active}
                    onCheckedChange={(checked) => {
                      if (checked && !editor.clean_verified) {
                        toast.error('Verify clean lyrics first, then toggle visibility.');
                        return;
                      }
                      updateField('active', checked);
                    }}
                    disabled={!editor.clean_verified && !editor.active}
                    data-testid="admin-playlist-field-active"
                  />
                  <Label htmlFor="pl-active" className="cursor-pointer">
                    {editor.active ? 'Visible to public' : 'Hidden'}
                  </Label>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={closeEditor} disabled={saving} data-testid="admin-playlist-cancel">Cancel</Button>
                  <Button onClick={handleSave} disabled={saving} className="bg-[#004C54] hover:bg-[#003a40] text-white" data-testid="admin-playlist-save">
                    <Save className="h-4 w-4 mr-1.5" />
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
